import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FlaskConical, BarChart3, Dna, Sparkles, BookOpen, ChevronRight, Beaker, Leaf, Box, ArrowLeftRight, Mountain } from 'lucide-react';
import SharedNav from '@/components/SharedNav';
import UsageMeter from '@/components/UsageMeter';

const RAMANUJAN_IMG = 'https://static.prod-images.emergentagent.com/jobs/b48706df-2eab-4a16-a663-66b78a54e225/images/094a8d2d6c09331dea5ebcfbc86ec8a32fbaa5d661539a59753c700bc2822134.png';
const ROSALIND_IMG = 'https://static.prod-images.emergentagent.com/jobs/b48706df-2eab-4a16-a663-66b78a54e225/images/6ca69a02f1c4771acf71f9909564c03ec7855dfa5768bf1eba0b0da08e5a9fed.png';

const CARDS = [
  { label: 'Experimental Designs', desc: 'CRD, RBD, Factorial, Split-Plot, Latin Square, Alpha Lattice & more', path: '/research-design', icon: FlaskConical, gradient: 'linear-gradient(135deg, #2D6A4F, #1B4332)' },
  { label: 'Biometrical Analysis', desc: 'Mating Designs, Genetic Parameters, Association, Multivariate, Stability', path: '/biometrical-analysis', icon: BarChart3, gradient: 'linear-gradient(135deg, #0D9488, #065F46)' },
  { label: 'Molecular Genetics', desc: 'Diversity Analysis, QTL Mapping, GWAS, Genomic Selection', path: '/molecular-genetics', icon: Dna, gradient: 'linear-gradient(135deg, #059669, #047857)' },
  { label: 'PhenoScan', desc: 'Image & sensor phenotyping — leaf, seed, root, panicle, disease, NIR, heatmap, integrated', path: '/phenoscan', icon: Leaf, gradient: 'linear-gradient(135deg, #40916C, #1B4332)' },
  { label: '3D Reconstruction', desc: 'Structure-from-motion 3D plants, volume, architecture — coming in next release', path: '/3d-reconstruction', icon: Box, gradient: 'linear-gradient(135deg, #14532D, #052E16)' },
  { label: 'Clean & Transform', desc: 'Outlier Detection, Data Validation & Transformation', path: '/data-cleaning', icon: Sparkles, gradient: 'linear-gradient(135deg, #D97706, #B45309)' },
  { label: 'BK Intelligence', desc: 'AI Assistant, Write My Paper, Glossary, Research Guides', path: '/bk-intelligence', icon: BookOpen, gradient: 'linear-gradient(135deg, #7C3AED, #5B21B6)' },
  { label: 'Predictive Models', desc: 'Machine Learning-based Yield Prediction & Trait Forecasting', path: '#', icon: Beaker, gradient: 'linear-gradient(135deg, #4338CA, #3730A3)', soon: true },
];

// Post-analysis cross-tools (Sub-Batches 3P + 3Q).  Distinct from the
// main feature grid because these don't START an analysis — they
// operate on analyses you have already completed.
const POST_ANALYSIS_TOOLS = [
  {
    label: 'Pipeline Comparison',
    desc: 'Compare two seasons / two locations by BLUP rank correlation (Falconer 1996; Kang 1988)',
    path: '/pipeline-comparison',
    icon: ArrowLeftRight,
    accent: '#2D6A4F',
  },
  {
    label: 'Response Surface 3D',
    desc: 'Interactive 3D surface of the factor-by-factor cell-means grid (Box & Wilson 1951)',
    path: '/response-surface-3d',
    icon: Mountain,
    accent: '#0D9488',
  },
];

function WaveDivider() {
  return (
    <div className="relative" style={{ marginTop: -1 }}>
      <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block" preserveAspectRatio="none" style={{ height: 60 }}>
        <path d="M0 40C240 70 480 10 720 40C960 70 1200 10 1440 40V80H0V40Z" fill="var(--page-bg)" />
      </svg>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ background: 'var(--page-bg)' }} data-testid="dashboard-page">
      <SharedNav />

      {/* Hero Section */}
      <div
        className="relative overflow-hidden grain-overlay"
        style={{
          background: 'linear-gradient(180deg, #1B4332 0%, #2D6A4F 100%)',
          minHeight: 320,
        }}
      >
        {/* Decorative circles */}
        <div className="absolute top-12 left-[15%] w-64 h-64 rounded-full opacity-[0.06]"
          style={{ background: 'radial-gradient(circle, #40916C, transparent)' }} />
        <div className="absolute bottom-0 right-[20%] w-48 h-48 rounded-full opacity-[0.05]"
          style={{ background: 'radial-gradient(circle, #B7E4C7, transparent)' }} />

        {/* Content */}
        <div className="relative z-10 max-w-[1320px] mx-auto px-6 flex flex-col items-center justify-center" style={{ paddingTop: 72, paddingBottom: 64 }}>
          <p
            className="font-body text-center mb-4"
            style={{ fontSize: 13, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(183,228,199,0.7)' }}
          >
            Biometrical Knowledge & Agricultural Research
          </p>
          <h1
            className="font-display text-center text-white"
            style={{ fontSize: 'clamp(36px, 5vw, 52px)', lineHeight: 1.15, marginBottom: 16 }}
          >
            Enjoy Your Research
          </h1>
          <p
            className="font-body text-center max-w-xl"
            style={{ fontSize: 18, fontWeight: 300, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}
          >
            Publication-ready analysis with interactive charts, path diagrams, dendrograms, and AI-powered insights
          </p>
        </div>
      </div>

      {/* Wave Divider */}
      <WaveDivider />

      {/* Freemium usage meter (hidden/unlimited for Pro) */}
      <UsageMeter />

      {/* Feature Cards Grid */}
      <div className="max-w-[1320px] mx-auto px-6 page-enter" style={{ paddingTop: 16, paddingBottom: 48 }}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {CARDS.map((card, i) => {
            const Icon = card.icon;
            return (
              <button
                key={card.label}
                onClick={() => !card.soon && navigate(card.path)}
                className={`group relative text-left rounded-2xl bg-white border transition-all fade-in ${card.soon ? 'opacity-60 cursor-not-allowed' : 'card-lift cursor-pointer'}`}
                style={{
                  borderColor: 'var(--border-color)',
                  padding: 28,
                  boxShadow: 'var(--shadow-sm)',
                  animationDelay: `${i * 0.06}s`,
                }}
                data-testid={`nav-card-${card.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                  style={{ background: card.gradient }}
                >
                  <Icon size={22} className="text-white" />
                </div>
                <h3
                  className="font-body mb-1"
                  style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-dark)' }}
                >
                  {card.label}
                  {card.soon && (
                    <span className="ml-2 text-[10px] font-body font-semibold px-2 py-0.5 rounded-full" style={{ background: '#FEF3C7', color: '#B45309' }}>
                      Coming Soon
                    </span>
                  )}
                </h3>
                <p
                  className="font-body"
                  style={{ fontSize: 14, color: 'var(--text-medium)', lineHeight: 1.5 }}
                >
                  {card.desc}
                </p>
                {!card.soon && (
                  <ChevronRight
                    size={18}
                    className="absolute top-7 right-6 transition-all opacity-0 group-hover:opacity-60 group-hover:translate-x-1"
                    style={{ color: 'var(--text-medium)' }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Inspirational Quotes */}
      <div className="max-w-[1320px] mx-auto px-6 pb-12" data-testid="post-analysis-tools-section">
        <h2 className="font-body mb-4" style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--green-700)' }}>
          Cross-Analysis Tools
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
          {POST_ANALYSIS_TOOLS.map((tool) => {
            const Icon = tool.icon;
            return (
              <button
                key={tool.label}
                onClick={() => navigate(tool.path)}
                className="group text-left rounded-xl bg-white border card-lift cursor-pointer transition-all"
                style={{
                  borderColor: 'var(--border-color)',
                  padding: '18px 22px',
                  boxShadow: 'var(--shadow-sm)',
                }}
                data-testid={`post-tool-${tool.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className="flex items-center gap-4">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-transform group-hover:scale-110"
                    style={{ background: tool.accent }}
                  >
                    <Icon size={18} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-body" style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-dark)' }}>
                      {tool.label}
                    </h3>
                    <p className="font-body mt-0.5" style={{ fontSize: 12, color: 'var(--text-medium)', lineHeight: 1.4 }}>
                      {tool.desc}
                    </p>
                  </div>
                  <ChevronRight
                    size={16}
                    className="shrink-0 opacity-30 group-hover:opacity-60 transition-all group-hover:translate-x-1"
                    style={{ color: 'var(--text-medium)' }}
                  />
                </div>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div
            className="rounded-2xl overflow-hidden bg-white border"
            style={{ borderColor: 'var(--border-color)', boxShadow: 'var(--shadow-sm)' }}
            data-testid="ramanujan-quote-card"
          >
            <div className="p-6 flex gap-5 items-start">
              <img src={RAMANUJAN_IMG} alt="Srinivasa Ramanujan" className="w-16 h-16 rounded-xl object-cover shrink-0" style={{ border: '2px solid var(--border-color)' }} />
              <div>
                <p className="font-body italic" style={{ fontSize: 14, color: 'var(--text-medium)', lineHeight: 1.6 }}>
                  "An equation for me has no meaning unless it expresses a thought of God."
                </p>
                <p className="font-body mt-3" style={{ fontSize: 13, fontWeight: 600, color: 'var(--green-700)' }}>
                  Srinivasa Ramanujan
                </p>
                <p className="font-body" style={{ fontSize: 11, color: 'var(--text-light)' }}>1887–1920 &bull; Mathematician Extraordinaire</p>
              </div>
            </div>
          </div>

          <div
            className="rounded-2xl overflow-hidden bg-white border"
            style={{ borderColor: 'var(--border-color)', boxShadow: 'var(--shadow-sm)' }}
            data-testid="rosalind-quote-card"
          >
            <div className="p-6 flex gap-5 items-start">
              <img src={ROSALIND_IMG} alt="Rosalind Franklin" className="w-16 h-16 rounded-xl object-cover shrink-0" style={{ border: '2px solid var(--border-color)' }} />
              <div>
                <p className="font-body italic" style={{ fontSize: 14, color: 'var(--text-medium)', lineHeight: 1.6 }}>
                  "Science and everyday life cannot and should not be separated."
                </p>
                <p className="font-body mt-3" style={{ fontSize: 13, fontWeight: 600, color: 'var(--green-700)' }}>
                  Rosalind Franklin
                </p>
                <p className="font-body" style={{ fontSize: 11, color: 'var(--text-light)' }}>1920–1958 &bull; Pioneer of DNA X-ray Crystallography</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Data Format Reference */}
      <div className="max-w-[1320px] mx-auto px-6 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-2xl bg-white border p-6" style={{ borderColor: 'var(--border-color)', boxShadow: 'var(--shadow-sm)' }}>
            <h3 className="font-body flex items-center gap-2 mb-3" style={{ fontSize: 14, fontWeight: 600, color: 'var(--green-900)' }}>
              <FlaskConical size={14} style={{ color: 'var(--green-500)' }} /> General Data Format
            </h3>
            <div className="rounded-lg p-3 font-mono text-xs overflow-x-auto" style={{ background: 'var(--page-bg)', border: '1px solid var(--border-color)' }}>
              <table className="w-full">
                <thead><tr style={{ color: 'var(--green-700)' }}><th className="px-2 text-left">Genotype</th><th className="px-2">Yield</th><th className="px-2">Height</th><th className="px-2">Days</th></tr></thead>
                <tbody style={{ color: 'var(--text-medium)' }}>
                  <tr><td className="px-2 font-body">G1</td><td className="px-2 text-center">45.2</td><td className="px-2 text-center">120.5</td><td className="px-2 text-center">85</td></tr>
                  <tr><td className="px-2 font-body">G2</td><td className="px-2 text-center">52.8</td><td className="px-2 text-center">135.2</td><td className="px-2 text-center">88</td></tr>
                </tbody>
              </table>
            </div>
            <p className="font-body mt-2" style={{ fontSize: 11, color: 'var(--text-light)' }}>Correlation, Regression, D², Path, PCA, DFA</p>
          </div>
          <div className="rounded-2xl bg-white border p-6" style={{ borderColor: 'var(--border-color)', boxShadow: 'var(--shadow-sm)' }}>
            <h3 className="font-body flex items-center gap-2 mb-3" style={{ fontSize: 14, fontWeight: 600, color: 'var(--green-900)' }}>
              <Dna size={14} style={{ color: 'var(--green-500)' }} /> Structured Designs
            </h3>
            <div className="rounded-lg p-3 font-mono text-xs overflow-x-auto" style={{ background: 'var(--page-bg)', border: '1px solid var(--border-color)' }}>
              <table className="w-full">
                <thead><tr style={{ color: 'var(--green-700)' }}><th className="px-2 text-left">Line</th><th className="px-2">Tester</th><th className="px-2">Rep</th><th className="px-2">Trait</th></tr></thead>
                <tbody style={{ color: 'var(--text-medium)' }}>
                  <tr><td className="px-2 font-body">L1</td><td className="px-2 text-center font-body">T1</td><td className="px-2 text-center">1</td><td className="px-2 text-center">45.2</td></tr>
                  <tr><td className="px-2 font-body">L1</td><td className="px-2 text-center font-body">T2</td><td className="px-2 text-center">1</td><td className="px-2 text-center">52.3</td></tr>
                </tbody>
              </table>
            </div>
            <p className="font-body mt-2" style={{ fontSize: 11, color: 'var(--text-light)' }}>Diallel, CRD, RBD, NC Design, AMMI, LxT</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t py-8 text-center" style={{ borderColor: 'var(--border-color)' }}>
        <p className="font-body" style={{ fontSize: 13, color: 'var(--text-light)' }}>
          BKAgriD &mdash; Biometrical Knowledge in Agriculture &bull; Research Made Beautiful
        </p>
      </div>
    </div>
  );
}
